import { useEffect, useState } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import toast from "react-hot-toast";
import { fr } from "yup-locales";
import { setLocale } from "yup";
import { logger } from "@/services/Logger";
import {
  cleanerError,
  convertPriceToCent,
  convertPriceFromCent,
} from "@/lib/utils";
import {
  MenuItemOption,
  MenuItemOptionFormData,
  MenuItemOptionValue,
  MenuItemOptionValueFormData,
  ValueDependentPriceFormData,
} from "@/redux/services/menu/menu.type";
import {
  useCreateOrUpdateMenuItemOptionMutation,
  useGetMenuItemOptionsQuery,
} from "@/redux/services/menu/menu.api";
import { QueryError } from "@/lib/type";
import { useAppSelector } from "@/redux/features/hooks";
import { toastApiError } from "@/lib/apiErrors";

setLocale(fr);

/**
 * Form validation schema for menu item options
 * Handles validation for template options, dependent options and their values
 */
const createOptionSchema = () => {
  return yup.object().shape({
    title: yup.string().required().label("Title"),
    type: yup.string().required().label("Type"),
    sortOrder: yup.number().min(1).required().label("Sort Order"),
    isTemplate: yup.boolean().label("Is Template"),
    shouldDisplayTitle: yup.boolean().label("Should Display Title"),
    templateDescription: yup.string().when("isTemplate", {
      is: true,
      then: () => yup.string().label("Template Description"),
      otherwise: () => yup.string().notRequired(),
    }),
    isDependantAnotherOption: yup.boolean(),
    dependsOnOption: yup.string().when("isDependantAnotherOption", {
      is: true,
      then: () => yup.string().required().label("Option dépendante"),
      otherwise: () => yup.string().notRequired(),
    }),
    menuItemOptionValues: yup.array().of(
      yup.object().shape({
        title: yup.string().required().label("Value Title"),
        sortOrder: yup.number().min(0).required().label("Value Sort Order"),
        basePrice: yup.number().when("isTemplate", {
          is: true,
          then: () => yup.number().min(0).required().label("Prix"),
          otherwise: () => yup.number().notRequired(),
        }),
        valueDependentPrices: yup.array().of(
          yup.object().shape({
            dependsOnValue: yup.string(),
            priceModifier: yup.number().default(0),
          })
        ),
      })
    ),
  });
};

/**
 * Custom hook for managing menu item option CRUD operations
 * Handles form state, validation, API calls, and complex dependencies between options
 *
 * @param closeModal - Function to close the modal when operation completes
 * @param item - Existing menu item option to edit (undefined for create)
 * @returns Form handlers and state for the menu item option form
 */
const useCrudMenuItemOption = (
  closeModal: () => void,
  item?: MenuItemOption
) => {
  // State for template and dependency flags
  const [isTemplate, setIsTemplate] = useState(item?.isTemplate ?? false);
  const [hasDependency, setHasDependency] = useState(
    item?.dependsOnOption !== undefined
  );
  const [shouldDisplayTitle, setShouldDisplayTitle] = useState(
    item?.shouldDisplayTitle ?? true
  );
  const [parentOption, setParentOption] = useState<MenuItemOption | null>(null);

  // Get current restaurant and available options from the store
  const { currentRestaurant } = useAppSelector((state) => state.user);
  const { data: optionsData } = useGetMenuItemOptionsQuery({
    limit: 1000,
    page: 1,
    restaurantId: currentRestaurant?.id,
  });

  // API mutation hook
  const [createOrUpdate, { isLoading }] =
    useCreateOrUpdateMenuItemOptionMutation();

  // Initialize form with validation schema
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    clearErrors,
    control,
    setValue,
    getValues,
    watch,
  } = useForm<MenuItemOptionFormData>({
    // @ts-ignore
    resolver: yupResolver(createOptionSchema()),
    defaultValues: {
      title: item?.title ?? "",
      type: item?.type ?? "select",
      sortOrder: item?.sortOrder ?? 1,
      menuItemOptionValues: [],
      isTemplate: item?.isTemplate ?? false,
      templateDescription: item?.templateDescription ?? "",
      isDependantAnotherOption: item?.dependsOnOption !== undefined,
      dependsOnOption: item?.dependsOnOption?.id ?? "",
      shouldDisplayTitle: item?.shouldDisplayTitle ?? true,
    },
  });

  // Field array for managing option values
  const { fields, append, remove } = useFieldArray({
    control,
    name: "menuItemOptionValues",
  });

  // Clean errors on form state change
  useEffect(() => {
    if (errors) {
      cleanerError(errors, clearErrors);
    }
  }, [errors, clearErrors]);

  // Find and set parent option if this option depends on another
  useEffect(() => {
    if (item?.dependsOnOption && optionsData && !parentOption) {
      const dependent = optionsData.results.find(
        (opt) => opt.id === item.dependsOnOption?.id
      );

      if (dependent) {
        setParentOption(dependent);
        setValue("dependsOnOption", dependent.id);
      }
    }
  }, [item, optionsData, setValue, parentOption]);

  // Initialize form values when item changes
  useEffect(() => {
    if (!item) {
      resetFormToDefaults();
      return;
    }

    initializeFormFromItem(item);
  }, [item]);

  // Set up dependent price mappings when parent option changes
  useEffect(() => {
    initializeDependentPrices();
  }, [parentOption, item?.dependsOnOption, setValue, getValues]);

  /**
   * Reset form to default values for a new option
   */
  const resetFormToDefaults = () => {
    // Every piece of UI state that mirrors a form field is reset here too,
    // or the next "new option" form opens with the previous option's
    // checkboxes (SFRESTO-23).
    setIsTemplate(false);
    setHasDependency(false);
    setShouldDisplayTitle(true);
    setParentOption(null);

    reset({
      title: "",
      type: "select",
      sortOrder: 1,
      menuItemOptionValues: [],
      isTemplate: false,
      templateDescription: "",
      isDependantAnotherOption: false,
      dependsOnOption: "",
      shouldDisplayTitle: true,
    });
  };

  /**
   * Initialize form with values from existing item
   */
  const initializeFormFromItem = (item: MenuItemOption) => {
    setIsTemplate(item.isTemplate ?? false);
    setHasDependency(!!item.dependsOnOption);
    setShouldDisplayTitle(item.shouldDisplayTitle ?? true);

    setValue("title", item.title);
    setValue("type", item.type);
    setValue("sortOrder", item.sortOrder);
    setValue("isTemplate", item.isTemplate ?? false);
    setValue("templateDescription", item.templateDescription ?? "");
    setValue("isDependantAnotherOption", !!item.dependsOnOption);
    setValue("dependsOnOption", item.dependsOnOption?.id ?? "");
    setValue("shouldDisplayTitle", item.shouldDisplayTitle ?? true);

    if (item.menuItemOptionValues && item.menuItemOptionValues.length > 0) {
      const formattedValues = formatOptionValues(item);
      setValue("menuItemOptionValues", formattedValues);
    } else {
      setValue("menuItemOptionValues", []);
    }
  };

  /**
   * Format option values from API format to form format
   */
  const formatOptionValues = (item: MenuItemOption) => {
    return item.menuItemOptionValues.map((value) => {
      const formattedValue: MenuItemOptionValueFormData = {
        id: value.id,
        title: value.title,
        sortOrder: value.sortOrder,
      };

      if (item.isTemplate) {
        formattedValue.basePrice = value.basePrice
          ? convertPriceFromCent(value.basePrice)
          : 0;
      }

      return formattedValue;
    });
  };

  /**
   * Initialize dependent prices for option values
   */
  const initializeDependentPrices = () => {
    if (!item?.dependsOnOption || !parentOption || !item.menuItemOptionValues) {
      return;
    }

    const currentValues = getValues("menuItemOptionValues") || [];

    currentValues.forEach((_, index) => {
      if (item.menuItemOptionValues[index]) {
        const originalValue = item.menuItemOptionValues[index];

        const dependentPrices = mapDependentPrices(originalValue, parentOption);
        setValue(
          `menuItemOptionValues.${index}.valueDependentPrices`,
          dependentPrices
        );
      }
    });
  };

  /**
   * Map dependent prices from parent option values
   */
  const mapDependentPrices = (
    originalValue: MenuItemOptionValue,
    parentOption: MenuItemOption
  ) => {
    return (
      parentOption.menuItemOptionValues?.map((parentOptionValue) => {
        const existingPriceModifier = originalValue.valueDependentPrices?.find(
          (dependentPrice) => {
            return dependentPrice.dependsOnValue?.id === parentOptionValue.id;
          }
        );

        return {
          dependsOnValue: parentOptionValue.id,
          priceModifier: existingPriceModifier?.priceModifier
            ? convertPriceFromCent(existingPriceModifier.priceModifier)
            : 0,
        };
      }) ?? []
    );
  };

  /**
   * Handle change of isTemplate checkbox
   */
  const handleIsTemplateChange = (value: boolean) => {
    setIsTemplate(value);
    setValue("isTemplate", value);
    if (value) {
      // Template options can't depend on other options
      setHasDependency(false);
      setValue("isDependantAnotherOption", false);
      setValue("dependsOnOption", "");
    }
  };

  /**
   * Handle change of dependency checkbox
   */
  const handleDependencyChange = (value: boolean) => {
    setHasDependency(value);
    setValue("isDependantAnotherOption", value);
    if (value) {
      // Dependent options can't be templates
      setIsTemplate(false);
      setValue("isTemplate", false);
      setValue("templateDescription", "");
    } else {
      setParentOption(null);
      setValue("dependsOnOption", "");
    }
  };

  /**
   * Handle selection of parent option for dependency
   */
  const handleParentOptionChange = (e: React.FormEvent<HTMLSelectElement>) => {
    const optionId = e.currentTarget.value;
    if (!optionId) {
      setParentOption(null);
      setValue("dependsOnOption", "");
      return;
    }

    const option = optionsData?.results.find(
      (option) => option.id === optionId
    );
    if (!option) return;

    setParentOption(option);
    setValue("dependsOnOption", optionId);
    updateValueDependentPrices(option);
  };

  const handleShouldDisplayTitleChange = (value: boolean) => {
    setShouldDisplayTitle(value);
    setValue("shouldDisplayTitle", value);
  };

  /**
   * Update dependent prices for all option values when parent option changes
   */
  const updateValueDependentPrices = (parentOption: MenuItemOption) => {
    const currentValues = getValues().menuItemOptionValues ?? [];

    if (currentValues.length > 0) {
      currentValues.forEach((_, index) => {
        const valueDependentPrices =
          parentOption.menuItemOptionValues?.map((dependantValue) => ({
            dependsOnValue: dependantValue.id,
            priceModifier: 0,
          })) ?? [];

        setValue(
          `menuItemOptionValues.${index}.valueDependentPrices`,
          valueDependentPrices
        );
      });
    }
  };

  /**
   * Add a new option value to the form
   */
  const appendNewValue = () => {
    const newValue: MenuItemOptionValueFormData = {
      title: "",
      sortOrder: fields.length + 1,
    };

    if (isTemplate) {
      newValue.basePrice = 0;
    }

    if (hasDependency && parentOption) {
      newValue.valueDependentPrices =
        parentOption.menuItemOptionValues?.map((dependantValue) => ({
          dependsOnValue: dependantValue.id,
          priceModifier: 0,
        })) ?? [];
    }

    append(newValue);
  };

  /**
   * Prepare form data for submission
   */
  const prepareSubmissionData = (data: MenuItemOptionFormData) => {
    const submissionData = { ...data };

    // Handle template-specific fields
    if (!isTemplate) {
      delete submissionData.templateDescription;
      removeBasePriceFromValues(submissionData);
    } else {
      addIdsToValues(submissionData);
      convertBasePricesToCents(submissionData);
    }

    // Handle dependency-specific fields
    if (submissionData.dependsOnOption) {
      formatDependencyData(submissionData);
    } else {
      delete submissionData.dependsOnOption;
      removeDependentPricesFromValues(submissionData);
    }

    return submissionData;
  };

  /**
   * Convert base prices to cents for template options
   */
  const convertBasePricesToCents = (data: MenuItemOptionFormData) => {
    if (data.menuItemOptionValues) {
      data.menuItemOptionValues = data.menuItemOptionValues.map((value) => {
        if (value.basePrice !== undefined) {
          return {
            ...value,
            basePrice: convertPriceToCent(value.basePrice),
          };
        }
        return value;
      });
    }
  };

  /**
   * Remove base price from values for non-template options
   */
  const removeBasePriceFromValues = (data: MenuItemOptionFormData) => {
    if (data.menuItemOptionValues) {
      data.menuItemOptionValues = data.menuItemOptionValues.map((value) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { basePrice, ...rest } = value;

        if (value.id) {
          return {
            ...rest,
            "@id": `/api/menu_item_option_values/${value.id}`,
          };
        }
        return rest;
      });
    }
  };

  /**
   * Add IDs to values for template options
   */
  const addIdsToValues = (data: MenuItemOptionFormData) => {
    if (data.menuItemOptionValues) {
      data.menuItemOptionValues = data.menuItemOptionValues.map((value) => {
        const result = { ...value };
        if (value.id) {
          result["@id"] = `/api/menu_item_option_values/${value.id}`;
        }
        return result;
      });
    }
  };

  /**
   * Format dependency data for submission
   */
  const formatDependencyData = (data: MenuItemOptionFormData) => {
    data.dependsOnOption = `/api/menu_item_options/${data.dependsOnOption}`;

    if (data.menuItemOptionValues) {
      data.menuItemOptionValues = data.menuItemOptionValues.map((value) => {
        const result = {
          ...value,
          ...(value.id && {
            "@id": `/api/menu_item_option_values/${value.id}`,
          }),
        };

        if (value.valueDependentPrices) {
          result.valueDependentPrices = formatDependentPrices(value);
        }
        return result;
      });
    }
  };

  /**
   * Format dependent prices for submission
   */
  const formatDependentPrices = (value: MenuItemOptionValueFormData) => {
    if (!value.valueDependentPrices) return [];

    return value.valueDependentPrices.map((dv: ValueDependentPriceFormData) => {
      const originalDependentPrice = item?.menuItemOptionValues
        ?.find((mov) => mov.id === value.id)
        ?.valueDependentPrices?.find(
          (dependantPrice) =>
            dependantPrice.dependsOnValue?.id === dv.dependsOnValue
        );

      return {
        dependsOnValue: `/api/menu_item_option_values/${dv.dependsOnValue}`,
        priceModifier: dv.priceModifier
          ? convertPriceToCent(dv.priceModifier)
          : 0,
        ...(originalDependentPrice?.id && {
          "@id": `/api/value_dependent_prices/${originalDependentPrice.id}`,
        }),
      };
    });
  };

  /**
   * Remove dependent prices from values
   */
  const removeDependentPricesFromValues = (data: MenuItemOptionFormData) => {
    if (data.menuItemOptionValues) {
      data.menuItemOptionValues = data.menuItemOptionValues.map((value) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { valueDependentPrices, ...rest } = value;
        if (value.id) {
          return {
            ...rest,
            "@id": `/api/menu_item_option_values/${value.id}`,
          };
        }
        return rest;
      });
    }
  };

  const close = () => {
    resetFormToDefaults();
    closeModal();
  };

  /**
   * Submit form data to API
   */
  const onSubmit = async (data: MenuItemOptionFormData) => {
    try {
      const submissionData = prepareSubmissionData(data);

      const res = await createOrUpdate({
        id: item?.id,
        data: submissionData,
      });

      if ("data" in res) {
        toast.success(
          `Option ${item?.id ? "modifiée" : "ajoutée"} avec succès`
        );
        close();
      } else if ("error" in res) {
        handleSubmissionError(res.error as QueryError);
      }
    } catch (error) {
      logger.error("useCrudMenuItemOption", "Error submitting form:", error);
      toast.error("Une erreur est survenue lors de la soumission");
    }
  };

  /**
   * Handle API submission errors
   */
  const handleSubmissionError = (error: QueryError) => {
    toastApiError(error, "Une erreur inconnue est survenue", {
      messages: {
        "validation.menu_item_option.error.title.exists":
          "Une option avec ce titre existe déjà",
      },
    });
  };

  return {
    register,
    errors,
    onSubmit: handleSubmit(onSubmit),
    isLoading,
    control,
    fields,
    append,
    remove,
    isTemplate,
    handleIsTemplateChange,
    hasDependency,
    handleDependencyChange,
    shouldDisplayTitle,
    handleShouldDisplayTitleChange,
    options: optionsData?.results?.map((option) => ({
      label: option.title,
      value: option.id,
    })),
    parentOption,
    handleParentOptionChange,
    appendNewValue,
    getValues,
    watch,
    close,
  };
};

export default useCrudMenuItemOption;
